/**
 * 첨부 용량·개수·보관 기간 정책 클라이언트 스토어
 *
 * 관리자 콘솔에서 정한 값을 클라이언트가 알아야 파일 선택·드롭 즉시 사전 차단을 할 수 있다.
 * 두 경로로 갱신된다:
 *  - pull: 부팅 + 소켓 재연결 시 GET /api/config/upload-policy
 *  - push: 관리자가 바꾸면 소켓 'config:upload-policy' (socketService.ts → App.tsx)
 *
 * Context가 아니라 모듈 레벨 스토어인 이유: ChatWindow의 addFiles가 `useCallback(..., [])`이고,
 * 그 addFiles를 Tauri 드롭 useEffect가 의존성으로 갖는다. 정책을 prop/context로 넣으면 값이
 * 바뀔 때마다 네이티브 드롭 리스너가 해제·재등록된다. 콜백 본문에서 동기 게터를 부르면 deps가 빈 채로 남는다.
 * 반응성이 필요한 화면(도움말·관리자)은 useUploadPolicy() 훅을 쓴다.
 */
import { useSyncExternalStore } from 'react';
import { apiFetch } from './apiClient';

export interface UploadPolicy {
  maxFileSizeMB: number;
  maxFiles: number;
  /** 첨부 보관 기간(일). 0 = 만료 없음. 화면 안내용이며 실제 만료 시각은 서버가 정한다 */
  retentionDays: number;
}

// server/services/uploadPolicy.js의 LIMITS와 같은 값.
// 조작된 소켓 payload가 사전 검증을 무력화하지 못하도록 클라이언트에서도 접는다.
const BOUNDS = {
  minFileSizeMB: 1, maxFileSizeMB: 200,
  minFiles: 1, maxFiles: 30,
  minRetentionDays: 0, maxRetentionDays: 3650,
};

// 값을 못 받았을 때의 기본값 = 서버 기본값. 최종 판정은 서버(multer)가 하므로 stale해도 안전하다.
const FALLBACK: UploadPolicy = { maxFileSizeMB: 50, maxFiles: 10, retentionDays: 90 };

let current: UploadPolicy = FALLBACK;
const listeners = new Set<() => void>();

function clamp(value: unknown, min: number, max: number, fallback: number): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

/** 동기 getter — addFiles가 의존성 없이 읽는다 */
export function getUploadPolicy(): UploadPolicy {
  return current;
}

export function getMaxFileSizeBytes(): number {
  return current.maxFileSizeMB * 1024 * 1024;
}

export function setUploadPolicy(next: Partial<UploadPolicy> | null | undefined): void {
  if (!next) return;
  const parsed: UploadPolicy = {
    maxFileSizeMB: clamp(next.maxFileSizeMB, BOUNDS.minFileSizeMB, BOUNDS.maxFileSizeMB, current.maxFileSizeMB),
    maxFiles: clamp(next.maxFiles, BOUNDS.minFiles, BOUNDS.maxFiles, current.maxFiles),
    retentionDays: clamp(next.retentionDays, BOUNDS.minRetentionDays, BOUNDS.maxRetentionDays, current.retentionDays),
  };
  // ⚠ 값이 같으면 참조를 그대로 둔다. 매번 새 객체를 만들면 useSyncExternalStore가 무한 렌더한다.
  // ⚠ 비교 대상에 새 필드를 빠뜨리면 그 필드만 바뀐 소켓 푸시가 조용히 무시된다.
  if (parsed.maxFileSizeMB === current.maxFileSizeMB
    && parsed.maxFiles === current.maxFiles
    && parsed.retentionDays === current.retentionDays) return;
  current = parsed;
  listeners.forEach(listener => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** 부팅·재연결 시 pull. 실패해도 기본값으로 계속 동작한다(최종 판정은 서버). */
export async function fetchUploadPolicy(): Promise<void> {
  try {
    setUploadPolicy(await apiFetch<UploadPolicy>('/config/upload-policy'));
  } catch (err) {
    console.warn('[UploadPolicy] 정책 조회 실패, 기본값을 유지합니다:', err);
  }
}

/** 값 변화에 반응해야 하는 화면(도움말·관리자 콘솔)용 */
export function useUploadPolicy(): UploadPolicy {
  return useSyncExternalStore(subscribe, getUploadPolicy, getUploadPolicy);
}
